import {
	useEditorEffect,
	useEditorEventCallback,
	useEditorEventListener,
} from "@handlewithcare/react-prosemirror";
import type { Key } from "@react-types/shared";
import type { EditorView } from "prosemirror-view";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useListBox, useOption } from "react-aria";
import { createPortal } from "react-dom";
import { Item, useListState } from "react-stately";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { AddIcon } from "@moment/icons";
import {
	buildPageLinkText,
	useCreatePage,
	useDocumentFromCurrentRoute,
	usePageFromCurrentRoute,
} from "@moment/state";
import { cn } from "@moment/ui";

import { PageLinkPageIcon } from "../../nodeViews/PageLink/PageLinkPageIcon";
import {
	type PageLinkAutocompleteState,
	closePageLinkAutocomplete,
	getAutocompleteSnapshot,
	insertPageLink,
	subscribeToAutocomplete,
} from "./pageLinkAutocompletePlugin";

interface SuggestionItem {
	type: "page" | "create";
	page?: PageMetadata;
	/** The display title shown in the autocomplete menu */
	title: string;
	/** The link text to insert (includes "Parent/Title" for subpages) */
	linkText: string;
	/** The parent page title, shown for subpages */
	parentTitle?: string;
}

const MAX_PAGE_SUGGESTIONS = 10;

const inactiveState: PageLinkAutocompleteState = {
	active: false,
	query: "",
	from: 0,
	to: 0,
	dismissed: null,
};

/** Get unique key for a suggestion item */
const getItemKey = (item: SuggestionItem) =>
	item.type === "page" ? `page-${item.page?.id}` : "create";

/** Option component using React Aria's useOption for accessibility */
interface OptionProps {
	item: { key: Key; value: SuggestionItem; rendered: React.ReactNode };
	state: ReturnType<typeof useListState<SuggestionItem>>;
	onSelect: (item: SuggestionItem) => void;
	onHover: () => void;
	isFocused: boolean;
}

const Option: React.FC<OptionProps> = ({ item, state, onSelect, onHover, isFocused }) => {
	const ref = useRef<HTMLLIElement>(null);
	const { optionProps } = useOption({ key: item.key }, state, ref);

	useEffect(() => {
		if (!isFocused) {
			return;
		}

		ref.current?.scrollIntoView({ block: "nearest" });
	}, [isFocused]);

	const suggestion = item.value;
	const page = suggestion.page;
	const documentId = useDocumentFromCurrentRoute().documentId;

	return (
		<li
			{...optionProps}
			ref={ref}
			onClick={() => onSelect(suggestion)}
			onMouseEnter={onHover}
			className={cn(
				"relative",
				"flex",
				"w-full",
				"cursor-default",
				"select-none",
				"items-center",
				"gap-2",
				"rounded",
				"py-1.5",
				"px-2",
				"text-sm",
				"outline-none",
				"transition-none",
				"[&_svg]:shrink-0",
				{
					"[&_svg]:text-palette-gray-11": !isFocused,
					"[&_svg]:text-fixed-light-default": isFocused,
					"[&_.text-secondary]:text-fixed-light-default": isFocused,
					"bg-palette-brand-9": isFocused,
					"text-fixed-light-default": isFocused,
				}
			)}
		>
			{suggestion.type === "page" ? (
				<>
					{page && documentId ? (
						<PageLinkPageIcon
							documentId={documentId}
							page={page}
							className="h-4 w-4 text-secondary"
						/>
					) : null}
					<span className="truncate">{suggestion.title}</span>
					{suggestion.parentTitle && (
						<span className="text-secondary text-xs truncate">
							in {suggestion.parentTitle}
						</span>
					)}
				</>
			) : (
				<>
					<AddIcon className="h-4 w-4 text-secondary" />
					<span className="truncate">Create &ldquo;{suggestion.title}&rdquo;</span>
				</>
			)}
		</li>
	);
};

export const PageLinkSuggestions: React.FC = () => {
	const { documentId, documentMetadata } = useDocumentFromCurrentRoute();
	const { pageId: currentPageId } = usePageFromCurrentRoute();
	const createPage = useCreatePage();

	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
	const [selectedIndex, setSelectedIndex] = useState(0);
	// Store the view in state so we can re-subscribe when it becomes available.
	// Using state instead of just a ref ensures React re-renders and re-subscribes
	// when the view is first captured.
	const [view, setView] = useState<EditorView | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const allPages = documentMetadata?.pagesMetadata;
	const previousPageIdRef = useRef(currentPageId);

	// Capture the EditorView from the ProseMirror context.
	// This runs on every editor update, keeping the state current.
	useEditorEffect((editorView) => {
		// Only update state if the view instance actually changed
		setView((prev) => (prev === editorView ? prev : editorView));
	});

	// Subscribe to autocomplete state changes using useSyncExternalStore.
	// This provides proper React 18 concurrent mode support and eliminates
	// the race conditions from the previous module-level listener Set.
	const state = useSyncExternalStore(
		useCallback(
			(onStoreChange) => {
				if (!view) {
					return () => {};
				}
				return subscribeToAutocomplete(view, onStoreChange);
			},
			[view] // Re-subscribe when view changes
		),
		useCallback(() => {
			if (!view) {
				return inactiveState;
			}
			return getAutocompleteSnapshot(view);
		}, [view]),
		() => inactiveState
	);

	// Update position when autocomplete becomes active or query changes
	useEffect(() => {
		if (!view) {
			setPosition(null);
			return;
		}

		if (state.active) {
			// Position follows the current cursor (to), not the [[ anchor (from),
			// so the dropdown moves when text wraps to a new line
			const coords = view.coordsAtPos(state.to);

			// Clamp to viewport so the dropdown doesn't overflow offscreen
			const menuWidth = 300; // max-w-[300px]
			const menuHeight = 300; // max-h-[300px]
			const top = Math.min(coords.bottom + 4, window.innerHeight - menuHeight - 8);
			const left = Math.min(Math.max(0, coords.left), window.innerWidth - menuWidth - 8);

			setPosition({ top, left });
			setSelectedIndex(0);
		} else {
			setPosition(null);
		}
	}, [view, state.active, state.to]);

	// Get filtered suggestions
	const suggestions = useMemo<SuggestionItem[]>(() => {
		if (!allPages) {
			return [];
		}

		const originalQuery = state.query.trim();
		const normalizedQuery = originalQuery.toLowerCase();
		// Filter out archived pages and pages without titles (to avoid producing invalid [[]] links)
		const validPages = allPages.filter(
			(page) => !page.archived && page.title && page.id !== currentPageId
		);

		// Filter pages by normalized query
		const filteredPages = normalizedQuery
			? validPages.filter((page) => page.title.toLowerCase().includes(normalizedQuery))
			: validPages;
		const visiblePages = filteredPages.slice(0, MAX_PAGE_SUGGESTIONS);

		const items: SuggestionItem[] = visiblePages.map((page) => {
			const linkText = buildPageLinkText(page.id, allPages);
			const displayTitle = page.title || "Untitled";

			// For subpages, show the parent title in the UI
			let parentTitle: string | undefined;
			if (page.parentPageID) {
				const parent = allPages.find((p) => p.id === page.parentPageID);
				parentTitle = parent?.title || "Untitled";
			}

			return {
				type: "page" as const,
				page,
				title: displayTitle,
				linkText,
				parentTitle,
			};
		});

		// Add "Create new page" option if query doesn't exactly match an existing page
		const exactMatch = validPages.some((page) => page.title.toLowerCase() === normalizedQuery);
		if (normalizedQuery && !exactMatch) {
			items.push({
				type: "create",
				title: originalQuery,
				linkText: originalQuery, // Raw title - escaping happens during markdown serialization
			});
		}

		return items;
	}, [allPages, currentPageId, state.query]);

	// Create list state for React Aria accessibility
	const listState = useListState<SuggestionItem>({
		items: suggestions,
		children: (item) => <Item key={getItemKey(item)}>{item.title}</Item>,
		selectionMode: "single",
	});

	const listBoxRef = useRef<HTMLUListElement>(null);
	const { listBoxProps } = useListBox(
		{
			"aria-label": "Page suggestions",
		},
		listState,
		listBoxRef
	);

	// Reset selected index when suggestions change
	useEffect(() => {
		setSelectedIndex(0);
	}, [suggestions.length]);

	useEffect(() => {
		const suggestion = suggestions[selectedIndex];
		if (!suggestion) {
			listState.selectionManager.setFocusedKey(null);
			return;
		}

		listState.selectionManager.setFocusedKey(getItemKey(suggestion));
	}, [listState.selectionManager, selectedIndex, suggestions]);

	// Handle keyboard events
	const handleKeyDown = useEditorEventCallback((view, event: KeyboardEvent) => {
		if (!state.active) {
			return;
		}

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
				break;
			case "ArrowUp":
				event.preventDefault();
				setSelectedIndex((prev) => Math.max(prev - 1, 0));
				break;
			case "Enter":
			case "Tab":
				event.preventDefault();
				if (suggestions[selectedIndex]) {
					handleSelect(suggestions[selectedIndex]);
				}
				break;
			case "Escape":
				event.preventDefault();
				closePageLinkAutocomplete(view);
				break;
		}
	});

	// Attach keyboard listener
	useEffect(() => {
		if (!state.active) {
			return;
		}

		const listener = (event: KeyboardEvent) => {
			if (
				event.key === "ArrowDown" ||
				event.key === "ArrowUp" ||
				event.key === "Enter" ||
				event.key === "Tab" ||
				event.key === "Escape"
			) {
				handleKeyDown(event);
			}
		};

		document.addEventListener("keydown", listener, true);
		return () => document.removeEventListener("keydown", listener, true);
	}, [state.active, handleKeyDown]);

	useEffect(() => {
		if (!view || !state.active) {
			return;
		}

		const closeAutocomplete = () => {
			closePageLinkAutocomplete(view);
		};

		const shouldKeepOpen = (target: EventTarget | null) => {
			if (!(target instanceof Node)) {
				return false;
			}

			return view.dom.contains(target) || containerRef.current?.contains(target) === true;
		};

		const handlePointerDown = (event: PointerEvent) => {
			if (!shouldKeepOpen(event.target)) {
				closeAutocomplete();
			}
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				closeAutocomplete();
			}
		};

		document.addEventListener("pointerdown", handlePointerDown, true);
		window.addEventListener("blur", closeAutocomplete);
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown, true);
			window.removeEventListener("blur", closeAutocomplete);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [view, state.active]);

	useEffect(() => {
		const previousPageId = previousPageIdRef.current;
		previousPageIdRef.current = currentPageId;

		if (!view || !state.active || previousPageId === currentPageId) {
			return;
		}

		closePageLinkAutocomplete(view);
	}, [view, state.active, currentPageId]);

	// Handle selection
	const handleSelect = useCallback(
		async (item: SuggestionItem) => {
			if (!view || !documentId) {
				return;
			}

			if (item.type === "page" && item.page) {
				// Insert link using the pre-computed link text (handles disambiguation)
				insertPageLink(view, item.linkText);
			} else if (item.type === "create") {
				// Capture the current position BEFORE the async operation.
				// If the user moves the cursor during page creation, we still want
				// to insert the link at the original position where they initiated it.
				const capturedPosition = { from: state.from, to: state.to };

				// Create a new page
				const newPage = await createPage({
					documentId,
					pageIndex: allPages?.length ?? 0,
					pageType: "markdown",
					pageMetadata: { title: item.title },
				});

				if (newPage) {
					// Use buildPageLinkText to properly escape slashes in the title.
					// Without this, a title like "A/B" would be stored unescaped and
					// resolvePageFromLinkText would misinterpret it as parent/child.
					const nextPages = [...(allPages ?? []), newPage];
					const linkText = buildPageLinkText(newPage.id, nextPages);
					insertPageLink(view, linkText || item.title, capturedPosition);
				}
			}
		},
		[view, documentId, allPages, createPage, state.from, state.to]
	);

	// Close when focus leaves the editor (e.g., clicking another dropdown in the app).
	// This mirrors the pattern used by EditorToolbar's focusout handler.
	useEditorEventListener("focusout", (view, event) => {
		if (
			!(event.relatedTarget instanceof HTMLElement) ||
			(!view.dom.contains(event.relatedTarget) &&
				!containerRef.current?.contains(event.relatedTarget))
		) {
			closePageLinkAutocomplete(view);
		}
	});

	if (!state.active || !position) {
		return null;
	}

	return createPortal(
		<div
			ref={containerRef}
			className="fixed z-popover min-w-[200px] max-w-[300px] overflow-hidden rounded-md border bg-primary-raised shadow-xl"
			style={{ top: position.top, left: position.left }}
		>
			{suggestions.length === 0 ? (
				<div className="px-3 py-2 text-sm text-secondary">No pages found</div>
			) : (
				<ul
					{...listBoxProps}
					ref={listBoxRef}
					className="w-full max-h-72 overflow-auto outline-none p-1"
				>
					{[...listState.collection].map((item, index) => (
						<Option
							key={item.key}
							item={{
								key: item.key,
								value: suggestions.find((s) => getItemKey(s) === item.key)!,
								rendered: item.rendered,
							}}
							state={listState}
							onSelect={handleSelect}
							onHover={() => setSelectedIndex(index)}
							isFocused={index === selectedIndex}
						/>
					))}
				</ul>
			)}
		</div>,
		document.body
	);
};
